// Leases — Company → Documents → Leases (N-110). Owner, 2026-09-26: "pull
// any lease info you can into the Lease table as well as any docs related
// to current locations that are lease docs … have them reflected in the
// Leases Table and document area for files."
//
// Mirrors lib/licenses.ts's own shape: one fetch under ONE key covering
// every clinic (not per-clinic like ServiceCompliance/Licenses), because
// this table renders all four clinics at once, grouped, rather than one
// clinic at a time.
import { authedGet, authedWrite } from './apiClient';
import { persistResource } from './store';
import { LOCATION_ORDER } from './locations';

export const LEASES_KEY = 'company-leases';

export type LeaseStatus = 'ACTIVE' | 'IN_EXTENSION' | 'EXPIRING' | 'EXPIRED' | 'TERMINATED';

export const LEASE_STATUSES: LeaseStatus[] = ['ACTIVE', 'IN_EXTENSION', 'EXPIRING', 'EXPIRED', 'TERMINATED'];

export const LEASE_STATUS_LABEL: Record<LeaseStatus, string> = {
  ACTIVE: 'Active',
  IN_EXTENSION: 'In extension',
  EXPIRING: 'Expiring',
  EXPIRED: 'Expired',
  TERMINATED: 'Terminated',
};

export type LeaseDocumentKind =
  | 'LEASE' | 'AMENDMENT' | 'EXTENSION' | 'ASSIGNMENT' | 'LOI' | 'GUARANTY'
  | 'SUBORDINATION' | 'NOTICE' | 'CAM_RECONCILIATION' | 'RENT_STATEMENT' | 'LETTER' | 'OTHER';

export const LEASE_DOCUMENT_KINDS: LeaseDocumentKind[] = [
  'LEASE', 'AMENDMENT', 'EXTENSION', 'ASSIGNMENT', 'LOI', 'GUARANTY',
  'SUBORDINATION', 'NOTICE', 'CAM_RECONCILIATION', 'RENT_STATEMENT', 'LETTER', 'OTHER',
];

export const LEASE_DOCUMENT_KIND_LABEL: Record<LeaseDocumentKind, string> = {
  LEASE: 'Lease',
  AMENDMENT: 'Amendment',
  EXTENSION: 'Extension',
  ASSIGNMENT: 'Assignment',
  LOI: 'LOI',
  GUARANTY: 'Guaranty',
  SUBORDINATION: 'Subordination',
  NOTICE: 'Notice',
  CAM_RECONCILIATION: 'CAM reconciliation',
  RENT_STATEMENT: 'Rent statement',
  LETTER: 'Letter',
  OTHER: 'Other',
};

export type LeaseDocumentDateBasis = 'SIGNED' | 'EFFECTIVE' | 'EMAIL' | 'FILE_NAME' | 'UNKNOWN';

export const LEASE_DOCUMENT_DATE_BASES: LeaseDocumentDateBasis[] = [
  'SIGNED', 'EFFECTIVE', 'EMAIL', 'FILE_NAME', 'UNKNOWN',
];

export const LEASE_DOCUMENT_DATE_BASIS_LABEL: Record<LeaseDocumentDateBasis, string> = {
  SIGNED: 'Signed',
  EFFECTIVE: 'Effective',
  EMAIL: 'Email date',
  FILE_NAME: 'From file name',
  UNKNOWN: 'Unknown',
};

export interface LeaseRow {
  id: number;
  locationCode: string;
  premises: string | null;
  shoppingCenter: string | null;
  landlord: string | null;
  landlordContact: string | null;
  managementCompany: string | null;
  leaseDate: string | null;
  commencementDate: string | null;
  expirationDate: string | null;
  termNote: string | null;
  baseRentMonthly: number | null;
  rentNote: string | null;
  optionsNote: string | null;
  status: LeaseStatus;
  nextActionDate: string | null;
  nextAction: string | null;
  notes: string | null;
  source: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface LeaseDocumentRow {
  id: number;
  leaseId: number | null;
  locationCode: string;
  kind: LeaseDocumentKind;
  title: string;
  docDate: string | null;
  dateBasis: LeaseDocumentDateBasis | null;
  driveFileId: string | null;
  driveUrl: string | null;
  url: string | null;
  sourcePath: string | null;
  sha256: string | null;
  note: string | null;
  addedAt: string;
  addedBy: string | null;
}

export interface LeasesData {
  leases: LeaseRow[];
  documents: LeaseDocumentRow[];
}

export function fetchLeases(): Promise<LeasesData> {
  return authedGet('/api/v1/company/leases');
}

export async function reloadLeases(): Promise<void> {
  const data = await fetchLeases();
  await persistResource(LEASES_KEY, data);
}

/** Every document filed against one lease (or against a clinic with no
 * lease row yet, when `leaseId` is null), oldest first. The server already
 * returns the whole list in that order (doc_date nulls last, then id) —
 * this re-sorts anyway rather than trusting fetch order to survive every
 * future caller (a local optimistic update, a different sort a page adds
 * later), the same "guards over trust" a fetched-and-filtered list gets
 * everywhere else in this app. */
export function documentsFor(
  documents: LeaseDocumentRow[],
  locationCode: string,
  leaseId: number | null,
): LeaseDocumentRow[] {
  return documents
    .filter((d) => d.locationCode === locationCode && d.leaseId === leaseId)
    .sort((a, b) => {
      if (a.docDate !== b.docDate) {
        if (a.docDate === null) return 1;
        if (b.docDate === null) return -1;
        return a.docDate < b.docDate ? -1 : 1;
      }
      return a.id - b.id;
    });
}

export interface LeaseGroup {
  clinic: string;
  /** null for a clinic with no lease row yet, or for a placeholder group
   * carrying documents filed before any lease row existed. */
  lease: LeaseRow | null;
  documents: LeaseDocumentRow[];
}

/**
 * One group per lease a clinic has, in LOCATION_ORDER — a clinic with no
 * lease row yet still gets one placeholder group (lease: null) so every
 * clinic always shows up, the same way the Company profile page always
 * renders all four. A document filed to a clinic before any lease row
 * existed (leaseId null) rides along with that clinic's placeholder group
 * when there is no lease row, or its own extra placeholder group when a
 * lease row exists too — it is never dropped for lack of a lease to sit
 * under.
 */
export function leaseGroups(leases: LeaseRow[], documents: LeaseDocumentRow[]): LeaseGroup[] {
  const groups: LeaseGroup[] = [];
  for (const clinic of LOCATION_ORDER) {
    const clinicLeases = leases.filter((l) => l.locationCode === clinic);
    const unattached = documentsFor(documents, clinic, null);
    if (clinicLeases.length === 0) {
      groups.push({ clinic, lease: null, documents: unattached });
      continue;
    }
    for (const lease of clinicLeases) {
      groups.push({ clinic, lease, documents: documentsFor(documents, clinic, lease.id) });
    }
    if (unattached.length > 0) {
      groups.push({ clinic, lease: null, documents: unattached });
    }
  }
  return groups;
}

export type LeaseWrite = {
  locationCode: string;
  premises: string;
  shoppingCenter: string;
  landlord: string;
  landlordContact: string;
  managementCompany: string;
  leaseDate: string;
  commencementDate: string;
  expirationDate: string;
  termNote: string;
  baseRentMonthly: string;
  rentNote: string;
  optionsNote: string;
  status: LeaseStatus;
  nextActionDate: string;
  nextAction: string;
  notes: string;
  source: string;
};

export async function addLease(body: LeaseWrite): Promise<{ lease: LeaseRow }> {
  return authedWrite('POST', '/api/v1/company/leases', body);
}

export async function updateLease(id: number, patch: Partial<LeaseWrite>): Promise<{ lease: LeaseRow }> {
  return authedWrite('PATCH', `/api/v1/company/leases/${id}`, patch);
}

export async function removeLease(id: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/company/leases/${id}`, {});
}

export type LeaseDocumentWrite = {
  kind: LeaseDocumentKind;
  title: string;
  docDate: string;
  dateBasis: LeaseDocumentDateBasis | '';
  driveFileId: string;
  url: string;
  sourcePath: string;
  sha256: string;
  note: string;
};

/** A document filed to a lease that already has a row. */
export async function addLeaseDocument(leaseId: number, body: LeaseDocumentWrite): Promise<{ document: LeaseDocumentRow }> {
  return authedWrite('POST', `/api/v1/company/leases/${leaseId}/documents`, body);
}

/** A document filed to a CLINIC before its lease row exists. */
export async function addLeaseDocumentForLocation(
  locationCode: string,
  body: LeaseDocumentWrite,
): Promise<{ document: LeaseDocumentRow }> {
  return authedWrite('POST', '/api/v1/company/leases/documents', { ...body, locationCode });
}

export async function updateLeaseDocument(id: number, body: LeaseDocumentWrite): Promise<{ document: LeaseDocumentRow }> {
  return authedWrite('PATCH', `/api/v1/company/leases/documents/${id}`, body);
}

export async function removeLeaseDocument(id: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/company/leases/documents/${id}`, {});
}

/** The latest instrument that set or changed the term — an amendment or an
 * extension after the lease itself (owner, 2026-09-26: the row showed the
 * 2007 contract date and the 2022 renewal hid inside the documents). Null
 * when nothing but the original lease is filed. */
export function latestRenewal(documents: LeaseDocumentRow[]): LeaseDocumentRow | null {
  // An extension is the instrument that moves the term; an amendment may be
  // (Lake Mary's Fourth Amendment) or may not (The Villages' trade-name
  // amendment, dated after its renewal). So: the latest EXTENSION when there
  // is one, else the latest AMENDMENT — a rule, not a reading of the title.
  const latestOf = (kind: string): LeaseDocumentRow | null => documents
    .filter((d): d is LeaseDocumentRow & { docDate: string } => d.kind === kind && d.docDate !== null)
    .sort((a, b) => (a.docDate < b.docDate ? 1 : a.docDate > b.docDate ? -1 : b.id - a.id))[0] ?? null;
  return latestOf('EXTENSION') ?? latestOf('AMENDMENT');
}

// --- expiration alerts (N-110 follow-on) ------------------------------------
//
// Owner, 2026-09-26, verbatim: "i need alert 12 month in advance and 6
// months in advance on all these lease expirations." src/lease_alerts.js
// carries the same 12/6 rule server-side (by day count, for the actual
// emails); this is the client's own read of it, in months, for the My Desk
// "Needs attention" panel and the Leases table's own hint under "Current
// term ends" — OWNER/DIRECTOR only, the same access Leases has everywhere
// else (leasesRoleGate).

/** The window: a lease within this many months of expiring shows up. */
export const LEASE_ATTENTION_MONTHS = 12;
/** Inside this many months, the hint/line gets the warn treatment. */
export const LEASE_WARN_MONTHS = 6;

/**
 * Whole calendar months from `today` to `expirationDate` — the same "age in
 * months" convention a birthday uses (11 months old means the 12th is not
 * complete yet): floor, never a partial month rounded up. Civil-date
 * COMPONENT arithmetic only (see client/src/lib/civilDate.ts's own rule) —
 * no `Date` object anywhere, so no timezone can move a day, let alone a
 * month. Can return a negative number for an expiration already past.
 */
export function leaseMonthsLeft(expirationDate: string, today: string): number {
  const [ey, em, ed] = expirationDate.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  let months = (ey - ty) * 12 + (em - tm);
  if (ed < td) months -= 1;
  return months;
}

/** Active (not EXPIRED/TERMINATED — already resolved, so they stop nagging)
 * leases with an expiration_date that is today or later and within
 * LEASE_ATTENTION_MONTHS, soonest first. Mirrors
 * src/lease_alerts.js's leaseAlertsDue's own status/date filter, in months
 * rather than a day count, for the panel line and its hasContent peek
 * (client/src/desk/panelCatalog.tsx). */
export function leasesNeedingAttention(leases: LeaseRow[], today: string): LeaseRow[] {
  return leases
    .filter((l): l is LeaseRow & { expirationDate: string } => (
      l.expirationDate !== null && l.status !== 'EXPIRED' && l.status !== 'TERMINATED'
    ))
    .filter((l) => {
      const months = leaseMonthsLeft(l.expirationDate, today);
      return months >= 0 && months <= LEASE_ATTENTION_MONTHS;
    })
    .sort((a, b) => leaseMonthsLeft(a.expirationDate, today) - leaseMonthsLeft(b.expirationDate, today));
}
